import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useInView } from 'react-intersection-observer';

const useStickShadow = (withBottomShadow: boolean) => {
    const ref = useRef<HTMLDivElement>(null);
    const [shadowY, setShadowY] = useState(0);

    const { ref: inViewRef, inView } = useInView({
        threshold: 1,
        skip: !withBottomShadow,
    });

    const setRefs = useCallback(
        (node: HTMLElement | null) => {
            // Ref's from useRef needs to have the node assigned to `current`
            ref.current = node as HTMLDivElement;
            // Callback refs, like the one from `useInView`, is a function that takes the node as an argument
            inViewRef(node);
        },
        [inViewRef],
    );

    useEffect(() => {
        if (!ref.current || !withBottomShadow) {
            return;
        }

        // :root 에 정의된 --navbar-height 값을 가져온다.
        const navbarHeight = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--navbar-height'),
        );

        // 부모 상대 기준으로 하는 sticky 여부는 자세히 파악하기 어렵기 때문에
        // navbar 밑에 붙었는지로 확인하고 그림자 라인을 붙여준다
        const handleScroll = () => {
            const rect = ref.current?.getBoundingClientRect();
            const top = rect?.top || 0;
            const bottom = rect?.bottom || 0;

            if (top === navbarHeight && bottom > 0) {
                setShadowY(bottom);
            } else {
                setShadowY(0);
            }
        };

        if (inView) {
            window.addEventListener('scroll', handleScroll);
        }

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [inView, withBottomShadow]);

    const shadowElement = useMemo(() => {
        return withBottomShadow && shadowY > 0 ? (
            <div
                className="fixed right-0 left-0 h-[1px] border-b shadow-sm"
                style={{ top: `${shadowY}px` }}
            ></div>
        ) : null;
    }, [withBottomShadow, shadowY]);

    return { setRefs, shadowElement };
};

/**
 * 부모 엘리먼트 수준에서 sticky
 * - 바로 위 부모 내에서만 스크롤 될 때 sticky 로 동작한다
 *   - 즉, 부모가 올라가면 따라서 올라감
 * - 부모가 relative 가 아니거나, 부모 아래에서 중첩되어 있으면 sticky 로 동작하지 않음
 * @see https://tailwindcss.com/docs/position#sticky-positioning-elements
 */
const Sticky = ({
    className,
    withBottomShadow = false,
    children,
}: {
    className?: string;
    /**
     * 하단에 그림자 라인을 표시할 지 여부
     */
    withBottomShadow?: boolean;
    children: React.ReactNode;
}) => {
    const { setRefs, shadowElement } = useStickShadow(withBottomShadow);

    return (
        <div
            ref={setRefs}
            className={twMerge('sticky top-(--navbar-height) z-(--z-index-sticky)', className)}
        >
            {children}
            {shadowElement}
        </div>
    );
};

export default Sticky;
